import { useCallback, useEffect, useState, useRef } from "react";
import { POSTER_SLOT_NAME, type PosterImageSource, type TeacherPosterBoard as Board, type TeacherPosterItem, type TeacherPosterStatus } from "@studio/shared";
import { InlineSpinner } from "../../components/InlineSpinner";
import { TeacherModal } from "../../components/TeacherChrome";
import { readTeacherTokensFromCookie, TeacherLoginRequiredError, teacherRequest } from "../../lib/teacherApi";
import { loadPosterFonts } from "../../poster/fonts";
import { PosterPreview } from "../../poster/PosterPreview";
import { preloadImages } from "../../poster/render";
import type { PdfPaper } from "../../poster/exportPdf";
import { starterDoc } from "../../poster/template";
import { bundlePages, printableStudents, type BundleMedia } from "../../poster/bundle";

/**
 * 교사 진행판 — 포스터 수업(목업 s5). 학생이 고치는 대로 몇 초 안에 바뀐다(5초마다 다시 읽는다).
 * 🔑 반 전체 PDF 는 **교사 브라우저**에서 만든다(학생 PDF 와 같은 함수 · 서버 부담 0).
 * 🔑 A2 묶음은 인쇄 확정한 포스터의 AI 그림에 인쇄용 사본을 쓴다(poster/bundle.ts).
 */

const STATUS_LABEL: Record<TeacherPosterStatus, string> = {
  confirmed: "인쇄 확정",
  editing: "편집 중",
  drawing: "그림 중",
  waiting: "시작 전",
};

const SLOT_TITLE = POSTER_SLOT_NAME;

/**
 * 교사 토큰으로 포스터 그림 바이트를 받는다(중계 D16 — 소유 교사만).
 * `print` = A2 인쇄용 사본(서버 업스케일). 아직 없으면 서버가 원본을 주고 `x-poster-print: pending`.
 */
async function teacherMedia(workId: string, assetId: string, print = false): Promise<BundleMedia> {
  const path = `/api/works/${encodeURIComponent(workId)}/poster/media/${encodeURIComponent(assetId)}${print ? "?print=1" : ""}`;
  const get = (token: string | undefined) => fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  let response = await get(readTeacherTokensFromCookie()?.access_token);
  if (response.status === 401) {
    // 토큰이 낡았다 — 교사 요청 하나로 새로 받고 다시 시도한다.
    await teacherRequest("/api/sessions").catch(() => undefined);
    response = await get(readTeacherTokensFromCookie()?.access_token);
  }
  if (!response.ok) return { blob: null, printPending: false };
  return { blob: await response.blob(), printPending: print && response.headers.get("x-poster-print") === "pending" };
}

function loaderFor(item: TeacherPosterItem) {
  return (source: PosterImageSource) => {
    const assetId = source.kind === "slot"
      ? item.cuts.find((cut) => cut.slot === source.slot)?.image_asset_id ?? null
      : source.asset_id;
    return assetId ? teacherMedia(item.work_id, assetId).then((media) => media.blob) : Promise.resolve(null);
  };
}

export function TeacherPosterBoard({ sessionId, onLoginRequired }: { sessionId: string; onLoginRequired: () => void }) {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<TeacherPosterItem | null>(null);
  const [bundling, setBundling] = useState<PdfPaper | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [gifted, setGifted] = useState<string | null>(null);
  const [classGifted, setClassGifted] = useState<string | null>(null);

  // 🩸 QA07(2026-10-08): 부모가 1초마다 시계를 갱신하며 새 `onLoginRequired` 를 넘기면 load 가 새로
  //    만들어지고, 아래 effect 가 즉시 다시 불러 5초 주기가 «약 1초» 가 됐다. 콜백은 ref 로만 읽는다.
  const loginRequiredRef = useRef(onLoginRequired);
  useEffect(() => {
    loginRequiredRef.current = onLoginRequired;
  }, [onLoginRequired]);

  const load = useCallback(async () => {
    try {
      const next = await teacherRequest<Board>(`/api/sessions/${encodeURIComponent(sessionId)}/posters`);
      // 🚨 늦게 온 다른 수업의 응답은 버린다 — 화면의 반과 선물을 받는 반이 어긋나면 안 된다.
      if (next.session.id !== sessionId) return;
      setBoard(next);
      setError(null);
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) loginRequiredRef.current();
      else setError("포스터를 불러오지 못했어요. 잠시 뒤 다시 시도해요.");
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!board) return <section className="poster-board">{error ? <p className="teacher-error-banner">{error}</p> : <p className="teacher-empty">포스터를 불러오는 중...</p>}</section>;

  const students = board.posters.filter((item) => !item.is_teacher);
  const count = (status: TeacherPosterStatus) => students.filter((item) => item.status === status).length;
  const usage = Math.min(100, Math.round(board.image_usage.used / Math.max(1, board.image_usage.cap) * 100));

  const act = async (key: string, path: string, body: unknown, method = "POST"): Promise<boolean> => {
    setActing(key);
    try {
      await teacherRequest(path, { method, body: JSON.stringify(body) });
      await load();
      return true;
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) onLoginRequired();
      else setError(caught instanceof Error ? caught.message : "처리하지 못했어요.");
      return false;
    } finally {
      setActing(null);
    }
  };
  /** 진행판에서 바로 선물(2026-10-04 jery) — 학생 표까지 내려가지 않아도 된다. 서버·횟수 규칙은 학생 표의 🎁 와 같다. */
  /**
   * 🚨 화면에 보이는 반과 주소의 반이 같을 때만 선물한다. 수업을 옮기는 사이 옛 반 카드가 보이는 채로
   *    눌리면 다른 반에 지급된다(Codex 검수 지적). 부모가 `key={sessionId}` 로 진행판을 새로 그리는 것과 겹쳐 막는다.
   */
  const sameClass = board.session.id === sessionId;
  const gift = async (item: TeacherPosterItem, kind: "image_regen" | "video_grant", amount: 2 | 3 | 5, label: string) => {
    if (!sameClass) return;
    setGifted(null);
    const ok = await act(`gift:${kind}:${amount}`, `/api/sessions/${encodeURIComponent(sessionId)}/gift`, { kind, amount, participant_id: item.participant_id });
    setGifted(ok ? `${item.student_name}에게 ${label} 보냈어요 ✓` : "선물을 보내지 못했어요. 다시 눌러 주세요.");
  };
  /**
   * 반 전체에 AI 영상 +2회(2026-10-12 jery — 용인신촌중 수업에서 강사님이 영상 선물을 못 찾았다).
   * 🔑 수업 전체의 영상 상한은 두지 않는다(jery 결정) — 서버에도 원래 없다. 학생 표의 「반 전체 선물」 과 같은 요청이다.
   */
  const giftVideoToClass = async () => {
    if (!sameClass) return;
    setClassGifted(null);
    const ok = await act("gift:class:video_grant:2", `/api/sessions/${encodeURIComponent(sessionId)}/gift`, { kind: "video_grant", amount: 2, all: true });
    setClassGifted(ok ? "반 전체에 AI 영상 +2회를 보냈어요 ✓" : "선물을 보내지 못했어요. 다시 눌러 주세요.");
  };
  const videoEnabled = board.posters.some((item) => item.video_enabled);
  const confirmedCount = students.filter((item) => item.status === "confirmed").length;

  const bundle = async (paper: PdfPaper) => {
    setBundling(paper);
    setProgress("글꼴을 준비하는 중...");
    try {
      await loadPosterFonts();
      const { buildPosterPdf, downloadBlob } = await import("../../poster/exportPdf");
      // 🔑 한 명씩 받아 그리고 내려놓는다(poster/bundle.ts) — 그래서 A2 도 인쇄용 사본을 쓸 수 있다.
      let pending = 0;
      const pages = (async function* () {
        for await (const page of bundlePages(printableStudents(students), paper, {
          fetchMedia: teacherMedia,
          preload: preloadImages,
          starterDoc,
          onProgress: (done, total) => setProgress(`포스터를 만드는 중 ${done}/${total}`),
        })) {
          pending += page.printPending;
          yield page;
        }
      })();
      const bytes = await buildPosterPdf(pages, paper, `${board.session.title} 포스터`, {
        ...(paper === "A2" ? {} : { maxImagePx: 3000 }),
        releaseImages: true,
      });
      setProgress("PDF 를 저장하는 중...");
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), `${board.session.title} 포스터 묶음 (${paper}).pdf`);
      setProgress(pending > 0 ? `고화질 그림 ${pending}장은 아직 만드는 중이라 보통 화질로 넣었어요. 1~2분 뒤 다시 받으면 더 선명해요.` : null);
    } catch (caught) {
      console.error("[poster-bundle]", caught);
      setProgress("묶음을 만들지 못했어요. 다시 눌러 주세요.");
    } finally {
      setBundling(null);
    }
  };

  return (
    <section className="poster-board" aria-label="반 전체 포스터">
      <div className="poster-board-head">
        <div>
          <h2>반 전체 포스터</h2>
          <p>학생이 고치는 대로 몇 초 안에 바뀌어요. 포스터를 누르면 크게 봐요.</p>
        </div>
        <div className="poster-board-actions">
          {progress ? <span className="poster-board-progress" role="status">{progress}</span> : null}
          {classGifted ? <span className="teacher-feedback" role="status">{classGifted}</span> : null}
          {videoEnabled && sameClass && board.session.status !== "ended" ? (
            <button className="button-secondary" type="button" disabled={acting !== null} onClick={() => void giftVideoToClass()}>
              🎁 반 전체 AI 영상 +2회
            </button>
          ) : null}
          <button className="button-secondary" type="button" disabled={bundling !== null || students.length === 0} onClick={() => void bundle("A4")}>
            {bundling === "A4" ? <InlineSpinner label="만드는 중..." /> : "A4 묶음"}
          </button>
          <button className="button-secondary" type="button" disabled={bundling !== null || students.length === 0} onClick={() => void bundle("A3")}>
            {bundling === "A3" ? <InlineSpinner label="만드는 중..." /> : "A3 묶음"}
          </button>
          <button className="button-primary" type="button" disabled={bundling !== null || students.length === 0} onClick={() => void bundle("A2")}>
            {bundling === "A2" ? <InlineSpinner label="만드는 중..." /> : "반 전체 PDF 받기 · A2"}
          </button>
        </div>
      </div>
      {board.session.status === "ended" ? (
        <div className="poster-reopen-bar">
          <span>수업이 끝났어요. 다시 열면 7일 동안 학생이 이어서 고칠 수 있어요.</span>
          <button className="button-secondary" type="button" disabled={acting !== null} onClick={() => void act("reopen", `/api/sessions/${encodeURIComponent(sessionId)}/reopen`, {})}>
            수업 다시 열기 (7일)
          </button>
        </div>
      ) : null}
      {board.session.class_ar.url ? (
        <div className="poster-reopen-bar poster-class-ar-bar">
          {board.session.class_ar.closed_at ? (
            <span>반 화면을 닫았어요. 지금은 포스터 QR 을 찍어도 이 반 포스터가 보이지 않아요.</span>
          ) : (
            <span>
              반 화면이 열려 있어요. 포스터 QR 을 찍으면 이 반 포스터를 모두 AR 로 볼 수 있어요.{" "}
              <a href={board.session.class_ar.url} target="_blank" rel="noreferrer">반 화면 열어 보기</a>
            </span>
          )}
          <button
            className="button-secondary"
            type="button"
            disabled={acting !== null || !sameClass}
            onClick={() => void act("class-ar", `/api/sessions/${encodeURIComponent(sessionId)}/class-ar`, { open: Boolean(board.session.class_ar.closed_at) })}
          >
            {acting === "class-ar" ? <InlineSpinner label="바꾸는 중..." /> : board.session.class_ar.closed_at ? "반 화면 다시 열기" : "반 화면 닫기"}
          </button>
        </div>
      ) : null}
      <div className="poster-board-stats">
        <article><span>인쇄 확정</span><strong>{count("confirmed")}</strong></article>
        <article><span>편집 중</span><strong>{count("editing")}</strong></article>
        <article><span>그림 만드는 중</span><strong>{count("drawing")}</strong></article>
        <article><span>시작 전</span><strong>{count("waiting")}</strong></article>
        <article><span>AR 로 볼 수 있음</span><strong>{confirmedCount} / {students.length}</strong></article>
        <article className="poster-board-usage">
          <span>오늘 studio 그림 사용량 <b>{board.image_usage.used} / {board.image_usage.cap}장</b></span>
          <div className="poster-usage-bar" aria-hidden="true"><i style={{ width: `${usage}%` }} /></div>
          <small>다른 그림·영상 수업과 함께 쓰는 하루 한도예요</small>
        </article>
      </div>
      {students.length === 0 ? <p className="teacher-empty">아직 들어온 학생이 없어요. QR 을 보여 주세요.</p> : null}
      <div className="poster-board-grid">
        {students.map((item) => (
          <button key={item.work_id} type="button" className="poster-board-card" onClick={() => setOpen(item)}>
            {item.doc || item.cuts.some((cut) => cut.image_url) ? (
              <PosterPreview state={item} />
            ) : (
              <div className="poster-board-empty">{item.status === "drawing" ? "그림 만드는 중" : "아직 시작 전"}</div>
            )}
            <span className="poster-board-name">
              <b>{item.student_name}</b>
              <em className={`poster-chip-status is-${item.status}`}>{STATUS_LABEL[item.status]}</em>
            </span>
          </button>
        ))}
      </div>
      {open ? (
        <TeacherModal title={`${open.student_name}의 포스터`} wide onClose={() => { setOpen(null); setGifted(null); }}>
          <div className="poster-board-detail">
            <div className="poster-board-detail-poster"><PosterPreview state={open} /></div>
            <div className="poster-board-detail-text">
              <p className="poster-board-detail-status">{STATUS_LABEL[open.status]} · 마지막 저장 {new Date(open.updated_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</p>
              {(() => {
                // 열어 둔 카드는 5초마다 새로 읽은 판에서 다시 찾는다 — 선물 뒤 남은 횟수가 바로 바뀐다.
                const live = board.posters.find((item) => item.work_id === open.work_id) ?? open;
                return (
                  <div className="poster-board-gift" aria-label="선물">
                    <p>
                      다시 그리기 <b>{live.image_regen_remaining}번</b> 남음
                      {live.video_enabled ? <> · AI 영상 <b>{live.video_remaining}번</b> 남음</> : null}
                    </p>
                    {board.session.status === "ended" ? (
                      <small>수업이 끝나서 선물을 보낼 수 없어요.</small>
                    ) : (
                      <div className="poster-board-gift-actions">
                        <button className="button-secondary" type="button" disabled={acting !== null} onClick={() => void gift(live, "image_regen", 3, "그림 +3회")}>🎁 그림 +3회</button>
                        <button className="button-secondary" type="button" disabled={acting !== null} onClick={() => void gift(live, "image_regen", 5, "그림 +5회")}>🎁 그림 +5회</button>
                        {live.video_enabled ? (
                          <button className="button-secondary" type="button" disabled={acting !== null} onClick={() => void gift(live, "video_grant", 2, "AI 영상 +2회")}>🎁 AI 영상 +2회</button>
                        ) : null}
                      </div>
                    )}
                    {gifted ? <span className="teacher-feedback" role="status">{gifted}</span> : null}
                  </div>
                );
              })()}
              <h3>학생이 그림을 그리려고 쓴 글</h3>
              {open.cuts.filter((cut) => cut.text).length === 0 ? <p className="teacher-empty">아직 없어요.</p> : null}
              {open.cuts.filter((cut) => cut.text).map((cut) => (
                <div key={cut.slot} className="poster-board-prompt">
                  <b>{SLOT_TITLE[cut.slot]}</b>
                  <p>{cut.text}</p>
                </div>
              ))}
            </div>
          </div>
        </TeacherModal>
      ) : null}
    </section>
  );
}
